<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import SettingsFieldSection from 'dashboard/components-next/Settings/SettingsFieldSection.vue';
import SettingsToggleSection from 'dashboard/components-next/Settings/SettingsToggleSection.vue';

const props = defineProps({
  options: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['update:options']);

const { t } = useI18n();

const reassignModeOptions = computed(() => [
  {
    value: 'preserve_if_valid',
    label: t(
      'INBOX_MGMT.CONVERSATION_TRANSFER.OPTIONS.REASSIGN_MODE.PRESERVE_IF_VALID'
    ),
  },
  {
    value: 'clear_all',
    label: t(
      'INBOX_MGMT.CONVERSATION_TRANSFER.OPTIONS.REASSIGN_MODE.CLEAR_ALL'
    ),
  },
]);

const reassignMode = computed(
  () => props.options.reassign_mode || 'preserve_if_valid'
);

const toggleOptions = computed(() => [
  {
    key: 'auto_assign_open',
    label: t('INBOX_MGMT.CONVERSATION_TRANSFER.OPTIONS.AUTO_ASSIGN_OPEN'),
  },
  {
    key: 'preserve_team_if_valid',
    label: t('INBOX_MGMT.CONVERSATION_TRANSFER.OPTIONS.PRESERVE_TEAM_IF_VALID'),
  },
  {
    key: 'preserve_sla',
    label: t('INBOX_MGMT.CONVERSATION_TRANSFER.OPTIONS.PRESERVE_SLA'),
  },
]);

const postTransferActions = computed(() => [
  {
    value: 'resolve',
    label: t('INBOX_MGMT.CONVERSATION_TRANSFER.OPTIONS.POST_TRANSFER.RESOLVE'),
    help: t(
      'INBOX_MGMT.CONVERSATION_TRANSFER.OPTIONS.POST_TRANSFER.RESOLVE_HELP'
    ),
    warning: t(
      'INBOX_MGMT.CONVERSATION_TRANSFER.OPTIONS.POST_TRANSFER.RESOLVE_WARNING'
    ),
  },
  {
    value: 'make_primary',
    label: t(
      'INBOX_MGMT.CONVERSATION_TRANSFER.OPTIONS.POST_TRANSFER.MAKE_PRIMARY'
    ),
    help: t(
      'INBOX_MGMT.CONVERSATION_TRANSFER.OPTIONS.POST_TRANSFER.MAKE_PRIMARY_HELP'
    ),
    warning: t(
      'INBOX_MGMT.CONVERSATION_TRANSFER.OPTIONS.POST_TRANSFER.MAKE_PRIMARY_WARNING'
    ),
  },
]);

const postTransferAction = computed(
  () => props.options.post_transfer_action || ''
);

const selectedActionWarning = computed(() => {
  const selected = postTransferActions.value.find(
    action => action.value === postTransferAction.value
  );
  return selected ? selected.warning : '';
});

const updateOptions = patch => {
  emit('update:options', { ...props.options, ...patch });
};

const onReassignModeChange = event => {
  updateOptions({ reassign_mode: event.target.value });
};

const onPostTransferActionChange = value => {
  updateOptions({ post_transfer_action: value });
};
</script>

<template>
  <div class="flex flex-col gap-4 mt-6 pt-4 border-t border-n-weak">
    <h3 class="text-heading-2 text-n-slate-12">
      {{ $t('INBOX_MGMT.CONVERSATION_TRANSFER.OPTIONS.TITLE') }}
    </h3>

    <SettingsFieldSection
      :label="
        $t('INBOX_MGMT.CONVERSATION_TRANSFER.OPTIONS.REASSIGN_MODE_LABEL')
      "
    >
      <select
        :value="reassignMode"
        :aria-label="
          $t('INBOX_MGMT.CONVERSATION_TRANSFER.OPTIONS.REASSIGN_MODE_LABEL')
        "
        class="!mb-0"
        @change="onReassignModeChange"
      >
        <option
          v-for="option in reassignModeOptions"
          :key="option.value"
          :value="option.value"
        >
          {{ option.label }}
        </option>
      </select>
    </SettingsFieldSection>

    <SettingsToggleSection
      v-for="option in toggleOptions"
      :key="option.key"
      :header="option.label"
      :model-value="Boolean(props.options[option.key])"
      @update:model-value="value => updateOptions({ [option.key]: value })"
    />

    <SettingsFieldSection
      :label="
        $t('INBOX_MGMT.CONVERSATION_TRANSFER.OPTIONS.POST_TRANSFER_LABEL')
      "
      class="[&>div]:!items-start"
    >
      <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <label
          v-for="action in postTransferActions"
          :key="action.value"
          class="flex items-start gap-3 p-4 rounded-xl outline outline-1 -outline-offset-1 cursor-pointer"
          :class="
            postTransferAction === action.value
              ? 'outline-n-brand'
              : 'outline-n-weak hover:outline-n-strong'
          "
        >
          <input
            type="radio"
            name="post-transfer-action"
            :value="action.value"
            :checked="postTransferAction === action.value"
            class="m-0 mt-1 flex-shrink-0 accent-n-brand"
            @change="onPostTransferActionChange(action.value)"
          />
          <span class="flex flex-col gap-1">
            <span class="text-heading-3 text-n-slate-12">
              {{ action.label }}
            </span>
            <span class="text-body-main text-n-slate-11">
              {{ action.help }}
            </span>
          </span>
        </label>
      </div>
    </SettingsFieldSection>

    <div
      v-if="selectedActionWarning"
      class="p-3 rounded-xl border border-n-amber-5 bg-n-amber-2"
    >
      <p class="text-sm text-n-amber-11">
        {{ selectedActionWarning }}
      </p>
    </div>
  </div>
</template>
